/**
 * Professions (อาชีพ, key 9): trade levels, recipes to craft, the goods pouch (use and sell) and the
 * merchant's seeds; plus the prompt over the skill bar at a resource node ([E] ตัดไม้ · ต้นสน) and
 * the progress of the work under way. Only sends commands and reads the world (shared professions.ts).
 */
import {
  CROPS,
  GOOD_LIST,
  GOODS,
  ITEMS,
  NODE_KINDS,
  PROFESSION_LIST,
  PROFESSIONS,
  plotRipe,
  RECIPE_LIST,
  tradeLevel,
  TOOLS,
  TRADE_MAX_LEVEL,
  type PlacedNode,
  type PlayerCommand,
  type ProfessionId,
  type RecipeDef,
  type World,
} from '@nine/shared';
import { currencyIcon, goodIcon, iconEl, itemIcon, professionIcon } from '../render/icons';
import { $, closeButton, h, setText, setWidth } from './dom';

/** A good's icon: 32 px for its own row, 16 px beside a recipe's inputs. */
const goodEl = (id: string, size: 16 | 32 = 32) => iconEl(goodIcon(id, size), `ico${size}`);

type Tab = 'trades' | 'craft' | 'goods' | 'shop';
const TABS: [Tab, string][] = [
  ['trades', 'อาชีพ'],
  ['craft', 'ทำของ'],
  ['goods', 'ถุงของ'],
  ['shop', 'ร้านเมล็ด'],
];
const CRAFTS: ProfessionId[] = ['blacksmith', 'tailor', 'cook', 'herbalist'];
const KIND_LABEL = { material: 'วัตถุดิบ', seed: 'เมล็ดพันธุ์', food: 'อาหาร', medicine: 'ยา' } as const;
/** How near the merchant the shop buttons work (World `nearMerchant`). */
const MERCHANT_RANGE = 5;

const fmt = (n: number) => Math.floor(n).toLocaleString('th-TH');

/** Grow time in Thai: hours and minutes ("3 ชม.", "45 นาที"), seconds only under a minute. */
export function growText(sec: number): string {
  const t = Math.max(0, Math.ceil(sec));
  if (t < 60) return `${t} วินาที`;
  const m = Math.ceil(t / 60);
  if (m < 60) return `${m} นาที`;
  const h = Math.floor(m / 60);
  const r = m % 60;
  return r ? `${h} ชม. ${r} นาที` : `${h} ชม.`;
}

export class TradesMenu {
  readonly root = h('div', { id: 'tradesMenu', hidden: true, role: 'dialog', ariaLabel: 'อาชีพ' });
  private readonly button = h(
    'button',
    { type: 'button', id: 'tradesBtn', title: 'อาชีพ: เก็บ ทำ ใช้ ขาย (9)' },
    h('i', { class: 'gm-mark' }, '商'),
    h('span', {}, 'อาชีพ'),
    h('kbd', {}, '9'),
  );
  private readonly prompt = h('div', { id: 'tradePrompt', hidden: true });
  private readonly promptText = h('span');
  private readonly promptBar = h('i');
  private tab: Tab = 'trades';
  private craftProf: ProfessionId = 'blacksmith';
  private lastKey = '';

  constructor(
    private readonly world: World,
    private readonly send: (c: PlayerCommand) => void,
  ) {
    this.button.addEventListener('pointerdown', (e) => {
      e.stopPropagation();
      this.toggle();
    });
    this.root.addEventListener('pointerdown', (e) => e.stopPropagation());
    this.prompt.append(this.promptText, h('div', { class: 'bar gold' }, this.promptBar));
    // The button sits in the HUD's menu (เมนู Esc); the window and the work prompt over the world.
    $('#gmPlay').append(this.button);
    $('#hud').append(this.root, this.prompt);
  }

  get isOpen(): boolean {
    return !this.root.hidden;
  }

  toggle(): void {
    this.root.hidden = !this.root.hidden;
    this.lastKey = '';
    if (this.isOpen) this.render();
  }

  close(): void {
    this.root.hidden = true;
  }

  /**
   * E at a resource node: harvest or gather it, or sow the best seed carried in an empty plot; while
   * working, put the work down. False when there is nothing to work here (E then talks to people).
   */
  workHere(): boolean {
    const t = this.world.trades;
    if (t.job) {
      this.send({ t: 'workStop' });
      return true;
    }
    const n = t.nodeNear(this.world.player);
    if (!n) return false;
    if (n.kind.id === 'plot' && !t.plot(n.id)) {
      const seed = t.seedsToPlant()[0];
      // With no seed to sow, the world says what the plot needs.
      this.send(seed ? { t: 'plant', node: n.id, seed: seed.seed } : { t: 'gather', node: n.id });
      return true;
    }
    this.send({ t: 'gather', node: n.id });
    return true;
  }

  /** Per frame: the node prompt and work progress; the window when what it shows changed. */
  sync(): void {
    this.syncPrompt();
    if (!this.isOpen) return;
    const pr = this.world.progress;
    const key = `${this.tab}|${this.craftProf}|${this.nearMerchant()}|${pr.gold}|${pr.inventory.length}|${JSON.stringify(pr.trades)}`;
    if (key === this.lastKey) return;
    this.lastKey = key;
    this.render();
  }

  // ── the prompt ──

  private describe(n: PlacedNode): string {
    const t = this.world.trades;
    const k = n.kind;
    if (k.id === 'plot') {
      const p = t.plot(n.id);
      if (!p) {
        const seed = t.seedsToPlant()[0];
        return seed ? `[E] ปลูก${seed.name} · ${k.name}` : `${k.name}ว่าง · ซื้อเมล็ดจากเถ้าแก่ฉาง`;
      }
      const crop = CROPS[p.crop];
      if (!crop) return k.name;
      return plotRipe(p)
        ? `[E] เกี่ยว${crop.name} · ${k.name}`
        : `${crop.name}กำลังโต ${Math.floor((p.grown / crop.grow) * 100)}% · อีก ${growText(crop.grow - p.grown)}`;
    }
    const prof = PROFESSIONS[k.prof];
    if (t.level(k.prof) < k.level) return `${k.name} · ต้องมี${prof.name} ขั้น ${k.level}`;
    if (n.left <= 0) return `${k.name}หมดแล้ว · ฟื้นในอีก ${Math.ceil(n.regrowT)} วินาที`;
    return `[E] ${k.verb} · ${k.name} (${prof.name} ขั้น ${t.level(k.prof)})`;
  }

  private syncPrompt(): void {
    const t = this.world.trades;
    const j = t.job;
    if (j) {
      const what =
        j.kind === 'craft'
          ? `${PROFESSIONS[j.prof].name}: ${this.recipeName(j.recipe ?? '')}${j.more ? ` (อีก ${j.more})` : ''}`
          : j.kind === 'plant'
            ? `กำลังปลูก${CROPS[j.seed ?? '']?.name ?? ''}`
            : `กำลัง${NODE_KINDS[t.node(j.node ?? '')?.kind.id ?? '']?.verb ?? 'ทำงาน'}`;
      this.prompt.hidden = false;
      this.prompt.classList.add('busy');
      setText(this.promptText, `${what}… · ขยับหรือกด E เพื่อหยุด`);
      setWidth(this.promptBar, j.t / j.total);
      return;
    }
    this.prompt.classList.remove('busy');
    const n = this.world.player.dead ? null : t.nodeNear(this.world.player);
    this.prompt.hidden = !n;
    if (n) setText(this.promptText, this.describe(n));
  }

  private recipeName(id: string): string {
    const r = RECIPE_LIST.find((x) => x.id === id);
    if (!r) return id;
    if ('tool' in r.out) return TOOLS[r.out.tool]?.name ?? r.out.tool;
    return 'item' in r.out
      ? (ITEMS[r.out.item]?.name ?? r.out.item)
      : (GOODS[r.out.good]?.name ?? r.out.good);
  }

  // ── the window ──

  render(): void {
    const tabs = h(
      'div',
      { class: 'em-tabs', role: 'tablist' },
      ...TABS.map(([t, label]) => {
        const b = h('button', { type: 'button', class: t === this.tab ? 'on' : '', role: 'tab' }, label);
        b.setAttribute('aria-selected', String(t === this.tab));
        b.addEventListener('click', () => {
          this.tab = t;
          this.lastKey = '';
          this.sync();
        });
        return b;
      }),
    );
    const close = closeButton(() => this.close());
    const body =
      this.tab === 'trades'
        ? this.tradesTab()
        : this.tab === 'craft'
          ? this.craftTab()
          : this.tab === 'goods'
            ? this.goodsTab()
            : this.shopTab();
    this.root.replaceChildren(
      h(
        'header',
        {},
        h('b', {}, 'อาชีพ'),
        h('small', {}, iconEl(currencyIcon('silver', 16), 'ico16'), `${fmt(this.world.progress.gold)} ตำลึง`),
        close,
      ),
      tabs,
      body,
    );
  }

  private button2(label: string, cmd: PlayerCommand, disabled = false, title = ''): HTMLButtonElement {
    const b = h('button', { type: 'button', class: 'btn small', disabled, title }, label);
    b.addEventListener('click', () => this.send(cmd));
    return b;
  }

  private tradesTab(): HTMLElement {
    const t = this.world.progress.trades;
    return h(
      'div',
      { class: 'tr-list' },
      ...PROFESSION_LIST.map((p) => {
        const lv = tradeLevel(t.xp[p.id] ?? 0);
        const fill = h('i');
        fill.style.width = lv.need ? `${(lv.into / lv.need) * 100}%` : '100%';
        return h(
          'div',
          { class: 'tr-row' },
          h('b', {}, iconEl(professionIcon(p.id), 'ico32'), p.name),
          h('span', { class: 'tr-lv' }, `ขั้น ${lv.level}${lv.level >= TRADE_MAX_LEVEL ? ' (สูงสุด)' : ''}`),
          h('div', { class: 'bar gold' }, fill),
          h('small', {}, lv.need ? `${fmt(lv.into)} / ${fmt(lv.need)}` : '—'),
          h('small', { class: 'tr-desc' }, p.desc),
        );
      }),
      h(
        'small',
        { class: 'em-hint' },
        'เดินไปที่ต้นไม้ หินมีสายแร่ ริมน้ำ กอสมุนไพร หรือแปลงนา แล้วกด E · ขยับหรือโดนตีงานจะถูกขัด',
      ),
    );
  }

  private recipeRow(r: RecipeDef): HTMLElement {
    const tr = this.world.trades;
    const why = tr.canCraft(r);
    const out =
      'tool' in r.out
        ? `${TOOLS[r.out.tool]?.name ?? r.out.tool} (เครื่องมือ)`
        : 'item' in r.out
        ? `${ITEMS[r.out.item]?.name ?? r.out.item} (อุปกรณ์)`
        : `${GOODS[r.out.good]?.name ?? r.out.good} ×${r.out.n}`;
    const inputs = r.inputs.map((i) =>
      h(
        'span',
        { class: tr.has(i.id) >= i.n ? 'ok' : 'short' },
        goodEl(i.id, 16),
        `${GOODS[i.id]?.name ?? i.id} ${tr.has(i.id)}/${i.n}`,
      ),
    );
    const outItem = 'item' in r.out ? ITEMS[r.out.item] : undefined;
    return h(
      'div',
      { class: `tr-recipe ${why ? 'off' : ''}` },
      h(
        'b',
        {},
        outItem ? iconEl(itemIcon(outItem), 'ico32') : 'good' in r.out ? goodEl(r.out.good) : null,
        out,
      ),
      h('small', {}, `ขั้น ${r.level} · ${r.time} วินาที · +${r.xp} แต้ม`),
      h('div', { class: 'tr-in' }, ...inputs),
      h(
        'div',
        { class: 'tr-btns' },
        this.button2('ทำ ×1', { t: 'craft', recipe: r.id, count: 1 }, !!why, why ?? ''),
        this.button2('×5', { t: 'craft', recipe: r.id, count: 5 }, !!why, why ?? ''),
      ),
    );
  }

  private craftTab(): HTMLElement {
    const pick = h(
      'div',
      { class: 'em-tabs' },
      ...CRAFTS.map((id) => {
        const b = h(
          'button',
          { type: 'button', class: id === this.craftProf ? 'on' : '' },
          iconEl(professionIcon(id, 16), 'ico16'),
          `${PROFESSIONS[id].name} ${this.world.trades.level(id)}`,
        );
        b.addEventListener('click', () => {
          this.craftProf = id;
          this.lastKey = '';
          this.sync();
        });
        return b;
      }),
    );
    return h(
      'div',
      { class: 'tr-list' },
      pick,
      ...RECIPE_LIST.filter((r) => r.prof === this.craftProf).map((r) => this.recipeRow(r)),
      h(
        'small',
        { class: 'em-hint' },
        'ทำได้ทุกที่ที่ไม่ได้ต่อสู้ · วัตถุดิบถูกหักเมื่อทำเสร็จแต่ละชิ้น · ชุดที่ทำได้ลงกระเป๋าและตีบวกได้',
      ),
    );
  }

  private nearMerchant(): boolean {
    const w = this.world;
    return Math.hypot(w.player.x - w.merchant.x, w.player.y - w.merchant.y) <= MERCHANT_RANGE;
  }

  private goodsTab(): HTMLElement {
    const tr = this.world.trades;
    const near = this.nearMerchant();
    const owned = GOOD_LIST.filter((g) => tr.has(g.id) > 0);
    if (!owned.length)
      return h(
        'div',
        { class: 'tr-list' },
        h('small', { class: 'em-hint' }, 'ถุงของยังว่าง · ไปเก็บของจากต้นไม้ หิน ริมน้ำ หรือแปลงนา'),
      );
    return h(
      'div',
      { class: 'tr-list' },
      ...owned.map((g) => {
        const n = tr.has(g.id);
        return h(
          'div',
          { class: 'tr-good' },
          h('b', {}, goodEl(g.id), `${g.name} ×${n}`),
          h('small', {}, `${KIND_LABEL[g.kind]} · ${g.desc} · ขาย ${g.price} ตำลึง`),
          h(
            'div',
            { class: 'tr-btns' },
            g.use ? this.button2('ใช้', { t: 'useGood', id: g.id }) : false,
            this.button2(
              'ขาย 1',
              { t: 'sellGood', id: g.id, n: 1 },
              !near,
              near ? '' : 'ต้องอยู่ใกล้เถ้าแก่ฉาง',
            ),
            this.button2(
              'ขายหมด',
              { t: 'sellGood', id: g.id, n },
              !near,
              near ? '' : 'ต้องอยู่ใกล้เถ้าแก่ฉาง',
            ),
          ),
        );
      }),
      h(
        'small',
        { class: 'em-hint' },
        near ? 'เถ้าแก่ฉางรับซื้อของทุกชนิด' : 'ขายของได้เมื่อยืนใกล้เถ้าแก่ฉาง (พ่อค้าเร่ในลาน)',
      ),
    );
  }

  private shopTab(): HTMLElement {
    const near = this.nearMerchant();
    const tr = this.world.trades;
    return h(
      'div',
      { class: 'tr-list' },
      ...GOOD_LIST.filter((g) => g.buy).map((g) => {
        const crop = CROPS[g.id];
        return h(
          'div',
          { class: 'tr-good' },
          h('b', {}, goodEl(g.id), `${g.name} · ${g.buy} ตำลึง`),
          h(
            'small',
            {},
            `${g.desc}${crop ? ` · ชาวนาขั้น ${crop.level} · โตใน ${growText(crop.grow)}` : ''} · มีอยู่ ${tr.has(g.id)}`,
          ),
          h(
            'div',
            { class: 'tr-btns' },
            this.button2('ซื้อ 1', { t: 'buyGood', id: g.id, n: 1 }, !near),
            this.button2('ซื้อ 5', { t: 'buyGood', id: g.id, n: 5 }, !near),
          ),
        );
      }),
      h(
        'small',
        { class: 'em-hint' },
        near ? 'เถ้าแก่ฉางขายเมล็ดและด้ายไหม' : 'ต้องยืนใกล้เถ้าแก่ฉาง (พ่อค้าเร่ในลาน) จึงซื้อได้',
      ),
    );
  }
}
